import {
  collection,
  doc,
  addDoc,
  getDocs,
  query,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from './firebase';

export type ProjectActivityType =
  | 'project_created'
  | 'state_changed'
  | 'contributor_added'
  | 'contributor_removed'
  | 'timeline_changed'
  | 'team_changed'
  | 'name_changed'
  | 'description_changed';

export interface ProjectActivity {
  id?: string;
  projectId: string;
  type: ProjectActivityType;
  actorId: string;
  actorName?: string;
  metadata?: {
    fromValue?: any;
    toValue?: any;
    contributorId?: string;
    contributorName?: string;
    [key: string]: any;
  };
  createdAt: Timestamp | ReturnType<typeof serverTimestamp>;
}

/**
 * Log a project activity event
 */
export async function logProjectActivity(
  orgId: string,
  activity: Omit<ProjectActivity, 'id' | 'createdAt'>
): Promise<string> {
  // Remove undefined values - Firestore doesn't allow them
  const activityData: any = {
    projectId: activity.projectId,
    type: activity.type,
    actorId: activity.actorId,
    createdAt: serverTimestamp()
  };

  // Only add optional fields if they have defined values
  if (activity.actorName !== undefined) {
    activityData.actorName = activity.actorName;
  }
  if (activity.metadata !== undefined) {
    // Deep clean metadata to remove undefined values
    const cleanMetadata: Record<string, any> = {};
    Object.keys(activity.metadata).forEach(key => {
      const value = activity.metadata![key];
      if (value !== undefined) {
        cleanMetadata[key] = value;
      }
    });
    // Only add metadata if it has any keys after cleaning
    if (Object.keys(cleanMetadata).length > 0) {
      activityData.metadata = cleanMetadata;
    }
  }

  const activityRef = await addDoc(
    collection(db, 'orgs', orgId, 'projects', activity.projectId, 'activities'),
    activityData
  );

  return activityRef.id;
}

/**
 * Get project activities in reverse chronological order
 */
export async function getProjectActivities(
  orgId: string,
  projectId: string,
  maxResults = 50
): Promise<ProjectActivity[]> {
  const activitiesQuery = query(
    collection(db, 'orgs', orgId, 'projects', projectId, 'activities'),
    orderBy('createdAt', 'desc'),
    limit(maxResults)
  );

  const snapshot = await getDocs(activitiesQuery);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as ProjectActivity[];
}

/**
 * Format activity for display
 */
export function formatActivityMessage(activity: ProjectActivity): string {
  const actor = activity.actorName || 'Someone';

  switch (activity.type) {
    case 'project_created':
      return `${actor} created the project`;

    case 'state_changed':
      if (activity.metadata?.fromLabel && activity.metadata?.toLabel) {
        return `${actor} changed state from ${activity.metadata.fromLabel} to ${activity.metadata.toLabel}`;
      }
      return `${actor} changed the project state`;

    case 'contributor_added':
      return `${actor} added ${activity.metadata?.contributorName || 'a contributor'}`;

    case 'contributor_removed':
      return `${actor} removed ${activity.metadata?.contributorName || 'a contributor'}`;

    case 'timeline_changed':
      return `${actor} updated the timeline`;

    case 'team_changed':
      if (activity.metadata?.toValue) {
        return `${actor} moved to ${activity.metadata.teamName || 'a team'}`;
      }
      return `${actor} removed from team`;

    case 'name_changed':
      return `${actor} renamed the project`;

    case 'description_changed':
      return `${actor} updated the description`;

    default:
      return `${actor} updated the project`;
  }
}
